/* eslint-disable camelcase */

/* eslint-disable max-statements */
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import ApprovedIcon from '../../assets/DisputeReviewedIcon.svg';
import DisputedIcon from '../../assets/DisputedIcon.svg';
import OpenDisputeNotificationIcon from '../../assets/Red_Dispute_Notification.png';
import { selectCurrentEmpid, selectIsDelegate } from '../../pages/login/authSlice';
import { useUpdatePayoutApprovalsMutation } from '../../pages/scorecardDashboard/scorecardDashboardSlice';
import { formatDate3 } from '../../utils/dateFormat';
import {
    ConstApplicationRole,
    ConstApprovalStatus,
    ConstApprovalWindowStatus,
    ConstPayoutApprovalStatus,
} from '../../utils/typeConstants';
import { AgentPayoutStatus } from '../../utils/types';
import { PayoutDetail } from '../agentPayoutDetail/AgentPayoutDetail';
import { colorCode, exportExcel, statusIndicatorIcon } from './metric-payout-table-service';
import './metricPayoutTable.scss';

type metricPayoutTableProps = {
    data: any;
    columns?: any;
    uniqueKey?: string;
    filterColumns?: any;
    scorecardroleid?: number;
    approvalGrid?: string;
    currentApprovalWindow?: number;
    scDisputesPill?: string;
    year?: number;
    month?: number;
    reviewStatusId?: number;
    scorecardVersion?: number;
};

export const MetricPayoutTable = ({
    data,
    columns,
    uniqueKey,
    filterColumns,
    scorecardroleid,
    approvalGrid,
    currentApprovalWindow,
    scDisputesPill,
    year,
    month,
    reviewStatusId,
    scorecardVersion,
}: metricPayoutTableProps) => {
    const isDelegate = useSelector(selectIsDelegate);
    const [updatePayoutApprovals] = useUpdatePayoutApprovalsMutation();
    const [expandedRows, setExpandedRows] = useState<any>();
    const loggedUserEmpId = useSelector(selectCurrentEmpid);
    const [selectedPayout, setSelectedPayout] = useState<any>([]);
    const toast = useRef<Toast>(null);
    const isMounted = useRef(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [diaApproval, setDiaApproval] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        const afilters = { ...filters };
        afilters.global.value = value;
        setFilters(afilters);
        setGlobalFilterValue(value);
    };
    const priceBodyTemplate = (rowData: any) => {
        if (rowData.scorecard_calculation_rule_id === 2 && rowData.payout_percentage != null) {
            return (
                <span>
                    {`${(rowData.payout_percentage * 100).toFixed(2)}% `}
                    <span style={{ color: '#6c757d', fontSize: '0.85em' }}>(${rowData.payout})</span>
                </span>
            );
        }
        return rowData.payout;
    };

    const metricsExceededBodyTemplate = (rowData: any) => {
        if (rowData.scorecard_calculation_rule_id !== 2) return null;
        const exceededCount = rowData.total_metrics_exceeded ?? 0;
        return (
            <div
                className="image-text"
                style={{
                    backgroundColor: colorCode(exceededCount),
                    color: 'white',
                    borderRadius: '30px',
                    textAlign: 'center',
                    width: '54px',
                }}
            >
                {exceededCount}
            </div>
        );
    };
    const agentNameBodyTemplate = (rowData: any) => {
        const agentName = `${rowData?.last_name}, ${rowData?.first_name}`;
        return <span className="image-text">{agentName}</span>;
    };

    const dateTemplate = (rowData: any) => {
        return <span>{formatDate3(rowData.date)}</span>;
    };

    const metricsMetBodyTemplate = (rowData: any) => {
        return (
            <div
                className="image-text"
                style={{
                    backgroundColor: colorCode(rowData?.total_metrics_attained),
                    color: 'white',
                    borderRadius: '30px',
                    textAlign: 'center',
                    width: '54px',
                }}
            >
                {rowData?.total_metrics_attained}
            </div>
        );
    };
    const allowExpansion = (rowData: any) => {
        if (rowData) return true;
        else return false;
    };

    const rowExpansionTemplate = (rowData: AgentPayoutStatus) => {
        return (
            <div className="orders-subtable">
                <PayoutDetail payout={rowData} scorecardroleid={scorecardroleid} />
            </div>
        );
    };

    useEffect(() => {
        isMounted.current = true;
    }, [data]);
    useEffect(() => {
        setSelectedPayout([]);
    }, [scorecardVersion, year, month]);
    useEffect(() => {
        if (filterColumns) {
            const filters: any = { global: { value: null, matchMode: FilterMatchMode.CONTAINS } };
            filterColumns.forEach((ele: any) => {
                const columnName = ele;
                filters[columnName] = { value: null, matchMode: FilterMatchMode.STARTS_WITH };
            });
            setFilters(filters);
        }
    }, [filterColumns]);
    const isRowSelectable = (event: any) => {
        const disallowedStatuses = [
            ConstApprovalStatus.DISPUTED,
            ConstApprovalStatus.MANAGER_APPROVED,
            ConstApprovalStatus.READY_FOR_EXTRACT,
            ConstApprovalStatus.CLOSED,
            ConstApprovalStatus.DISQUALIFIED,
        ];

        if (disallowedStatuses.includes(event.data.approval_status_id)) {
            return false; // https://ttec-pmo.atlassian.net/browse/E20-1810
        }

        if (approvalGrid === 'TeamPayout') {
            return (
                event.data.approval_status_id === ConstApprovalStatus.OPEN ||
                event.data.approval_status_id === ConstApprovalStatus.ASSOCIATE_APPROVED
            );
        } else if (approvalGrid === 'AssociatePayout') {
            return (
                reviewStatusId === 2 &&
                event.data.approval_status_id === ConstApprovalStatus.OPEN &&
                event.data.month === month &&
                event.data.year === year
            );
        } else if (approvalGrid === 'TeamLeadPayout') {
            return (
                event.data.month === month &&
                event.data.year === year &&
                event.data.scorecard_version_id === scorecardVersion &&
                event.data.approval_status_id === ConstApprovalStatus.OPEN
            );
        } else return false;
    };

    const dialogFuncMap = {
        diaApproval: setDiaApproval,
    };
    const onHide = () => {
        dialogFuncMap.diaApproval(false);
        setSelectedPayout([]);
    };
    const UpdatePayoutApproval = (payoutApprovals: any) => {
        updatePayoutApprovals(payoutApprovals)
            .unwrap()
            .then((response: any) => {
                const idGroups= response.data.split("~").filter((item:any) => item !== "");
                if(idGroups.length>0){
                const idList: any = []
                idGroups.forEach((group: string) => {
                    const ids = group.split(":")[0];
                    idList.push(ids);
                });
                const filteredData = data.filter((item: any) => idList.includes(item.emp_monthly_result_id.toString()));
                const filteredEmployees = filteredData.map((item: any) => item.last_name + ' ' + item.first_name);
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Warning Message',
                    detail: `Approvals for the following Employees are Failed: ${filteredEmployees.join(', ')}`,
                    life: 3000,
                });
                }
                else {
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'All Payouts approved successfully',
                        life: 3000,
                    });
                }
                setSelectedPayout([]);
            })
            .then((error: any) => {
                if (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error,
                        life: 3000,
                    });
                }
            });
    };
    const onTeamLeadPayoutApproveClick = () => {
        if (currentApprovalWindow === ConstApprovalWindowStatus.TEAM_LEAD_WINDOW) {
            if (scDisputesPill === 'Reviewed') {
                const empMonthlyResultIds: number[] = [];
                selectedPayout.forEach((element: any) => {
                    if (
                        element.month === month &&
                        element.year === year &&
                        element.scorecard_version_id === scorecardVersion
                    ) {
                        empMonthlyResultIds.push(element.emp_monthly_result_id);
                    }
                });
                const payoutApprovals = {
                    action_by: loggedUserEmpId,
                    action_name: 'Team Lead Approved',
                    emp_monthly_result_ids: empMonthlyResultIds,
                    approval_status_id: ConstApprovalStatus.TEAM_LEAD_APPROVED, //4 - Team Lead Approved
                    payout_approval_action_id: ConstPayoutApprovalStatus.APPROVED, // 2- Approved
                    application_role_id: ConstApplicationRole.TEAM_LEAD, //2 - Team Lead
                };
                UpdatePayoutApproval(payoutApprovals);
            } else {
                setDiaApproval(true);
            }
        } else {
            toast.current?.show({
                severity: 'info',
                summary: 'Info',
                detail: 'Team Lead can approve payout only during Team Lead Approval Window',
                life: 3000,
            });
            setSelectedPayout([]);
        }
    };
    const confirmAgentPayoutApproval = () => {
        confirmDialog({
            message: 'Are you sure?  Scorecard cannot be re-opened after approval.',
            header: '',
            icon: 'pi pi-exclamation-triangle',
            accept: () => onTeamPayoutApproveClick(),
        });
    };
    const onTeamPayoutApproveClick = () => {
        if (
            (approvalGrid === 'TeamPayout' &&
                currentApprovalWindow === ConstApprovalWindowStatus.TEAM_LEAD_WINDOW) ||
            (approvalGrid === 'AssociatePayout' &&
                currentApprovalWindow === ConstApprovalWindowStatus.ASSOCIATE_WINDOW)
        ) {
            let payoutApprovals = {};
            if (approvalGrid === 'TeamPayout') {
                const empMonthlyResultIds: number[] = [];
                selectedPayout.forEach((element: any) => {
                    empMonthlyResultIds.push(element.emp_monthly_result_id);
                });
                payoutApprovals = {
                    action_by: loggedUserEmpId,
                    action_name: 'Team Lead Approved',
                    emp_monthly_result_ids: empMonthlyResultIds,
                    approval_status_id: ConstApprovalStatus.TEAM_LEAD_APPROVED, //4 - Team Lead Approved
                    payout_approval_action_id: ConstPayoutApprovalStatus.APPROVED, // 2- Approved
                    application_role_id: ConstApplicationRole.TEAM_LEAD, //2 - Team Lead
                };
                UpdatePayoutApproval(payoutApprovals);
            } else if (approvalGrid === 'AssociatePayout') {
                const associateEmpMonthlyResultIds: number[] = [];
                selectedPayout.forEach((element: any) => {
                    if (
                        element.month === month &&
                        element.year === year &&
                        element.scorecard_version_id === scorecardVersion
                    ) {
                        associateEmpMonthlyResultIds.push(element.emp_monthly_result_id);
                    }
                });
                payoutApprovals = {
                    action_by: loggedUserEmpId,
                    action_name: 'Associate Approved',
                    emp_monthly_result_ids: associateEmpMonthlyResultIds,
                    approval_status_id: ConstApprovalStatus.ASSOCIATE_APPROVED,
                    payout_approval_action_id: ConstPayoutApprovalStatus.APPROVED,
                    application_role_id: ConstApplicationRole.ASSOCIATE,
                };
                UpdatePayoutApproval(payoutApprovals);
            }
        } else {
            const role = approvalGrid === 'TeamPayout' ? 'Team Lead' : 'Associate';
            toast.current?.show({
                severity: 'info',
                summary: 'Info',
                detail: `${role} can approve payout only during ${role} Approval Window`,
                life: 3000,
            });
            setSelectedPayout([]);
        }
    };

    const statusIndicator = (rowData: any) => {
        const icon = statusIndicatorIcon(rowData, approvalGrid);
        const alt: any = 'Approval Status';
        let statusIcon = '';
        if (icon === 'disputed') statusIcon = DisputedIcon;
        else if (icon === 'approved') statusIcon = ApprovedIcon;
        return (
            <div>
                {statusIcon ? <img className="justify-content-left " src={statusIcon} alt={alt} /> : ''}
            </div>
        );
    };
    const getTemplate = (template: string) => {
        switch (template) {
            case 'dateBodyTemplate':
                return dateTemplate;
            case 'priceBodyTemplate':
                return priceBodyTemplate;
            case 'metricsMetBodyTemplate':
                return metricsMetBodyTemplate;
            case 'metricsExceededBodyTemplate':
                return metricsExceededBodyTemplate;
            case 'agentNameBodyTemplate':
                return agentNameBodyTemplate;
            default:
                return template;
        }
    };
    const renderHeader = () => {
        return (
            <div className="flex justify-content-between exportexcel">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Keyword Search"
                    />
                </span>
                {approvalGrid === 'TeamPayout' || approvalGrid === 'AssociatePayout' ? (
                    <Button
                        type="button"
                        className="btn-primary float-end"
                        onClick={
                            approvalGrid === 'AssociatePayout'
                                ? confirmAgentPayoutApproval
                                : onTeamPayoutApproveClick
                        }
                        disabled={selectedPayout?.length === 0}
                    >
                        {approvalGrid === 'AssociatePayout' ? 'Approve Scorecard' : 'Approve Team Payout'}
                    </Button>
                ) : (
                    ''
                )}
                {approvalGrid === 'TeamLeadPayout' ? (
                    <Button
                        type="button"
                        className="btn-primary float-end"
                        onClick={onTeamLeadPayoutApproveClick}
                        disabled={selectedPayout?.length === 0}
                    >
                        Approve Payout
                    </Button>
                ) : (
                    ''
                )}
                {!isDelegate&&(<Button
                    type="button"
                    icon="pi pi-upload me-2"
                    className="btn-export float-end"
                    onClick={() => {
                        exportExcel(data);
                    }}
                    data-pr-tooltip="XLS"
                >
                    Export
                </Button>)}
            </div>
        );
    };

    const header = renderHeader();

    const approveDiaFooter = () => {
        return (
            <div className="text-center">
                <Button
                    className="p-button-outlined text-center borderRadius me-3"
                    label="Close"
                    onClick={onHide}
                />
            </div>
        );
    };

    // convert date dd-mm-yyyy to Date object to properly work sorting by date
    const processData = (data: any) => {
        return data.map((item: any) => {
            // date formt expected dd-mm-yyyy
            if (item?.date === undefined) return item;
            else {
                const [day, month, year] = item.date.split('-');
                return {
                    ...item,
                    date: new Date(`${month}-${day}-${year}`),
                };
            }
        });
    };

    return (
        <div className="card mt-3 ms-0 me-0 shadow bg-body rounded border-0">
            <div className="card-body p-0 align-middle text-center table">
                <Toast ref={toast} />
                <ConfirmDialog />
                <DataTable
                    value={processData(data)}
                    header={header}
                    expandedRows={expandedRows}
                    onRowToggle={(e) => {
                        setExpandedRows(e.data);
                    }}
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    paginator={data?.length > 5}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey={uniqueKey}
                    selectionMode="single"
                    selection={selectedPayout}
                    onSelectionChange={(e) => setSelectedPayout(e?.value)}
                    isDataSelectable={isRowSelectable}
                    className="mainTable mainTableFilterStyles"
                    globalFilterFields={filterColumns}
                    filters={filters}
                    onFilter={(e) => setFilters(e.filters as any)}
                    filterDisplay="row"
                    sortField={columns[0].field}
                    sortOrder={columns[0].field === 'date' ? -1 : 1}
                >
                    <Column
                        headerStyle={{ width: '1rem' }}
                        bodyStyle={{ width: '1rem' }}
                        body={statusIndicator}
                    />
                    <Column key="check" selectionMode="multiple" style={{ width: '3.5em' }} />

                    {isDelegate?  columns
                        .filter((column:any) => column.field !== 'payout'  ) // Filter out column with name 'payout'
                        .map((column: any) => {
                            return (
                                <Column
                                    key={column.id}
                                    field={column.field}
                                    header={column.name}
                                    body={getTemplate(column.template)}
                                    sortable
                                    filter={column.filter}
                                    filterPlaceholder={'Search'}
                                />
                            );
                        })
                        :
                        columns
                        .map((column: any) => {
                            return (
                                <Column
                                    key={column.id}
                                    field={column.field}
                                    header={column.name}
                                    body={getTemplate(column.template)}
                                    sortable
                                    filter={column.filter}
                                    filterPlaceholder={'Search'}
                                />
                            );
                        })
                        }

                    <Column
                        key="expand"
                        expander={allowExpansion}
                        style={{ width: '4em', marginRight: '10px' }}
                        hidden ={isDelegate}
                    />
                </DataTable>
            </div>
            {diaApproval ? (
                <Dialog
                    visible={diaApproval}
                    style={{ width: '20vw' }}
                    onHide={() => onHide()}
                    footer={approveDiaFooter}
                >
                    <div className="text-center">
                        <div>
                            <img src={OpenDisputeNotificationIcon} alt={OpenDisputeNotificationIcon} />
                        </div>
                        <span style={{ fontSize: '24px', fontWeight: '700', color: '#7D71E2' }}>
                            Open Disputes
                        </span>
                        <p>Open scorecard disputes should be closed prior to approval</p>
                    </div>
                </Dialog>
            ) : (
                ''
            )}
        </div>
    );
};
